import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import { ProjectGuard } from '../components/common/ProjectGuard';
import { useProjectStore } from '../stores/project.store';
import { useProject, type BookSummaryDto } from '../api/queries/projects';
import { useCreateBook } from '../api/queries/books';
import { formatDistanceToNow } from '../lib/dates';

export function BooksRoute() {
  const { activeProjectId } = useProjectStore();

  return (
    <ProjectGuard>
      {activeProjectId && <BooksListContent projectId={activeProjectId} />}
    </ProjectGuard>
  );
}

function BooksListContent({ projectId }: { projectId: string }) {
  const { data: project, isLoading, isError } = useProject(projectId);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const books = project?.books ?? [];

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Book Drafts{project ? ` \u2014 ${project.name}` : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New Draft
        </Button>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load project.</Alert>}

      <Grid container spacing={2}>
        {books.map((item: BookSummaryDto) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
            <Card
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                navigate({
                  to: '/books/$bookId/resume',
                  params: { bookId: item.id },
                })
              }
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {item.isPrimary && (
                    <Chip
                      size="small"
                      label={'\u2605 Primary'}
                      color="primary"
                    />
                  )}
                  <Typography variant="h6" noWrap>
                    {item.label || `Draft ${item.versionNumber}`}
                  </Typography>
                </Stack>
                {item.stage && (
                  <Typography variant="body2" color="text.secondary">
                    {item.stage}
                  </Typography>
                )}
                {item.sourceOutlineLabel && (
                  <Typography variant="caption" color="text.secondary">
                    from: {item.sourceOutlineLabel}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    size="small"
                    label={`${item.chapterCount} ch${item.chapterCount === 1 ? '' : 's'}`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(item.updatedAt)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isLoading && books.length === 0 && (
        <EmptyState
          icon={<MenuBookIcon sx={{ fontSize: 64 }} />}
          title="No book drafts yet"
          description="Create a new draft to start writing, or generate one from an outline."
          ctaLabel="New Draft"
          onCtaClick={() => setCreateOpen(true)}
        />
      )}

      <CreateDraftDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={projectId}
      />
    </Paper>
  );
}

function CreateDraftDialog({
  open,
  onClose,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
}) {
  const create = useCreateBook();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');

  const handleSubmit = async () => {
    if (!title.trim()) return;
    const result = await create.mutateAsync({
      projectId,
      title: title.trim(),
      pov: 'third_limited',
      tense: 'past',
    });
    setTitle('');
    onClose();
    navigate({
      to: '/books/$bookId/resume',
      params: { bookId: result.data.id },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New Draft</DialogTitle>
      <DialogContent>
        <TextField
          label="Draft label"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
          fullWidth
          sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!title.trim() || create.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
